import React, { forwardRef, useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

/**
 * A reusable React-Bootstrap Tooltip component.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.content - The content to display inside the tooltip.
 * @param {'top' | 'right' | 'bottom' | 'left'} [props.placement='top'] - The placement of the tooltip.
 * @param {React.ReactNode} props.children - The element(s) that the tooltip will be attached to.
 * @param {string} [props.id] - Optional: A custom ID for the tooltip. Defaults to an auto-generated ID.
 * @param {object} [props.overlayProps] - Optional: Additional props to pass to the OverlayTrigger.
 * @param {object} [props.tooltipProps] - Optional: Additional props to pass to the Tooltip.
 */
const CustomTooltip = forwardRef(
  ({ content, placement = 'top', children, id, overlayProps, tooltipProps, ...rest }, ref) => {
    // Generate a unique ID for the tooltip if not provided.
    // useId is a React 18+ hook. For older React, you might use a simple counter
    // or a library like uuid, but ensure it's client-side safe.
    const uniqueId = useId();
    const tooltipId = id || `tooltip-${uniqueId}`;

    const renderTooltip = (props) => (
      <Tooltip id={tooltipId} {...props} {...tooltipProps}>
        {content}
      </Tooltip>
    );

    return (
      <OverlayTrigger
        placement={placement}
        delay={{ show: 200, hide: 200 }} // Optional: Add a slight delay
        overlay={renderTooltip}
        {...overlayProps}
      >
        {/*
          It's crucial to correctly pass the ref and any other DOM props
          to the underlying element when using forwardRef, especially if children
          is a DOM element directly. If children is a React component, ensure it
          also forwards its ref correctly if you want this to work consistently.
        */}
        {React.Children.only(children)}
      </OverlayTrigger>
    );
  }
);

CustomTooltip.displayName = 'CustomTooltip'; // For better debugging in React DevTools

export default CustomTooltip;